import { useCallback, useMemo, useState } from "react";
import { ChevronDown, Gem } from "lucide-react";
import uniq from "lodash/uniq";
import isEmpty from "lodash/isEmpty";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import RevenueOpportunityLockStateDialog from "components/RevenueOpportunitiesTableV3/dialogs/RevenueOpportunityLockStateDialog";
import RevenueOpportunityStatusDialog from "components/RevenueOpportunitiesTableV3/dialogs/RevenueOpportunityStatusDialog";
import usePageTitle from "hooks/usePageTitle";
import { getNextQueryParams, buildPrice } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import RevenueOpportunityService from "services/RevenueOpportunities";
import UserService from "services/User";
import { useRevenueOpportunitiesTable } from "hooks/useRevenueOpportunitiesTable";
import NewRevenueOpportunityButton from "routes/revenue_opportunity/components/NewRevenueOpportunityButton";
import { omitByNil } from "helpers/tables";

export const REVENUE_OPPORTUNITIES_TABLE_CONSTANTS = {
  tableName: "revenue_opportunity",
  className: "RevenueOpportunity",
  exportTableName: "RevenueOpportunitiesTable",
  header: "Revenue Opportunities",
  headerIcon: Gem,
};

const STATUS_DIALOG = "status";
const LOCK_DIALOG = "lock";

export const renderTotalValueSum = extra =>
  extra?.total_value_sum != null ? (
    <> • Total Value Sum: {buildPrice(extra.total_value_sum)}</>
  ) : null;

const ActionButtons = ({
  selectedIds = [],
  selectedData = [],
  uncheckAll,
  invalidateData,
}) => {
  const campaignIds = selectedData.map(data => data.campaign_id);
  const uniqueCampaignIds = uniq(campaignIds);
  const hasLockedSelected = selectedData.some(data => data.is_locked);
  const isAdmin = UserService.isAdmin();
  const hasSelection = !isEmpty(selectedData);
  const isChangeStatusDisabled =
    !hasSelection || uniqueCampaignIds.length > 1 || hasLockedSelected;
  const campaignId = !isChangeStatusDisabled ? uniqueCampaignIds[0] : null;
  const [openDialog, setOpenDialog] = useState(null);
  const closeDialogUnlessOpening = isOpen => {
    if (!isOpen) setOpenDialog(null);
  };

  const handleFinish = async () => {
    uncheckAll();
    await invalidateData();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={!hasSelection}
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          Actions
          <ChevronDown />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw:w-auto">
          <Authorized
            permissions={[ACL_RELATIONSHIPS.revenueOpportunityStatus.edit]}
          >
            <DropdownMenuItem
              disabled={isChangeStatusDisabled}
              onClick={() => setOpenDialog(STATUS_DIALOG)}
            >
              Change Status
            </DropdownMenuItem>
          </Authorized>
          {isAdmin && (
            <DropdownMenuItem onClick={() => setOpenDialog(LOCK_DIALOG)}>
              Change Lock State
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <RevenueOpportunityStatusDialog
        open={openDialog === STATUS_DIALOG}
        onOpenChange={closeDialogUnlessOpening}
        revenueOpportunityIds={selectedIds}
        campaignId={campaignId}
        onSaved={handleFinish}
      />
      <RevenueOpportunityLockStateDialog
        open={openDialog === LOCK_DIALOG}
        onOpenChange={closeDialogUnlessOpening}
        revenueOpportunityIds={selectedIds}
        onSaved={handleFinish}
      />
    </>
  );
};

const RevenueOpportunitiesTableV3 = ({
  entityId,
  campaignId,
  caseId,
  contactId,
}) => {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.revenueOpportunityTags.create,
      ACL_RELATIONSHIPS.revenueOpportunityTags.read,
      ACL_RELATIONSHIPS.revenueOpportunityTags.delete,
    ],
    { partialMatch: true }
  );

  const isStandalone = !entityId && !campaignId && !caseId;
  usePageTitle(
    isStandalone ? REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.header : null
  );

  const defaultQueryParams = useMemo(() => {
    return omitByNil({
      entity_id: entityId,
      campaign_id: campaignId,
      case_id: caseId,
      contact_id: contactId,
    });
  }, [entityId, campaignId, caseId, contactId]);

  const {
    filters,
    columns,
    customFields,
    queryKey,
    invalidateData,
    checkForLockedRows,
  } = useRevenueOpportunitiesTable({
    entityId,
    campaignId,
    caseId,
    contactId,
    tableName: REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.tableName,
    className: REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.className,
  });

  const fetchRevenueOpportunities = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        const response =
          await RevenueOpportunityService.getRevenueOpportunitiesTable({
            ...nextQueryParams,
            ...defaultQueryParams,
          });
        return response;
      } catch (error) {
        console.error("Error fetching revenue opportunities:", error);
        throw error;
      }
    },
    [columns, defaultQueryParams]
  );

  const actionButtons = useCallback(
    ({ selectedIds, selectedData, uncheckAll }) => (
      <ActionButtons
        selectedIds={selectedIds}
        selectedData={selectedData}
        campaignId={campaignId}
        uncheckAll={uncheckAll}
        invalidateData={invalidateData}
      />
    ),
    [invalidateData, campaignId]
  );

  return (
    <BaseTableV3
      columns={columns}
      filters={filters}
      customFields={customFields}
      headerIcon={REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.headerIcon}
      header={REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.header}
      tableName={REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.tableName}
      queryKey={queryKey}
      className={REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.className}
      exportTableName={REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.exportTableName}
      downloadParams={defaultQueryParams}
      fetchData={fetchRevenueOpportunities}
      paginationExtra={renderTotalValueSum}
      actionButtons={actionButtons}
      invalidateData={invalidateData}
      createButton={
        <NewRevenueOpportunityButton
          entityId={entityId}
          campaignId={campaignId}
          caseId={caseId}
          contactId={contactId}
        />
      }
      enableSearch
      enableSettings
      tagsDropdownParams={{
        visible: enableTags,
        checkForDisabledRows: checkForLockedRows,
        disabledTooltip:
          "One or more selected revenue opportunities are locked.",
      }}
    />
  );
};

export default RevenueOpportunitiesTableV3;
